import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { findPerfectMatches } from '@/services/perfectMatchService';
import { transformDatabaseCamp, enrichDatabaseCampWithGoogleData } from '@/services/placesAPI';
import { getCategoryEmoji } from '@/utils/emojiHelpers';

/**
 * usePerfectMatches
 * Shared hook that prepares program data (optional enrichment), then runs the
 * matching algorithm and returns matches, loading and error state.
 */
export default function usePerfectMatches(
  userProfile,
  inputPrograms,
  options = {}
) {
  const {
    maxResults = 200,
    minScore = 60,
    limitTop = undefined,
    enrich = true,
  } = options;

  const [matches, setMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  const preparedProgramsPromise = useMemo(() => {
    // Return a promise so caller lifecycle is straightforward in one effect
    const prepare = async (programs) => {
      if (!programs || programs.length === 0) return [];

      if (!enrich) {
        // Minimal transform to normalize fields used by matcher
        return programs.map((camp) => {
          const transformed = transformDatabaseCamp ? transformDatabaseCamp(camp) : camp;
          if (transformed && !transformed.emoji) {
            try {
              transformed.emoji = getCategoryEmoji(transformed);
            } catch (_) {}
          }
          return transformed;
        });
      }

      try {
        // Enrich first 20 to avoid excessive API calls; transform all
        const first = programs.slice(0, 20);
        const rest = programs.slice(20);

        const enrichedFirst = await Promise.all(
          first.map(async (camp) => {
            try {
              // Check if enrichment is needed before calling API
              const needsEnrichment = !camp.photos || 
                                      !camp.user_ratings_total || 
                                      !camp.rating ||
                                      !camp.website ||
                                      !camp.formatted_phone_number;
              
              if (needsEnrichment) {
                console.log('🔍 Enriching perfect match (missing data):', camp.name || camp.display_name);
                const enriched = await enrichDatabaseCampWithGoogleData(camp);
                return enriched || camp;
              } else {
                console.log('✅ Perfect match already has complete data, skipping enrichment:', camp.name || camp.display_name);
                return camp;
              }
            } catch (_) {
              return camp;
            }
          })
        );

        const transformed = [...enrichedFirst, ...rest].map((camp) => {
          try {
            const t = transformDatabaseCamp ? transformDatabaseCamp(camp) : camp;
            if (!t.emoji) t.emoji = getCategoryEmoji(t);
            return t;
          } catch (_) {
            return camp;
          }
        });

        return transformed;
      } catch (e) {
        // If enrichment fails, still return minimally transformed programs
        return programs.map((camp) => {
          const transformed = transformDatabaseCamp ? transformDatabaseCamp(camp) : camp;
          if (transformed && !transformed.emoji) {
            try {
              transformed.emoji = getCategoryEmoji(transformed);
            } catch (_) {}
          }
          return transformed;
        });
      }
    };

    return prepare(inputPrograms);
  }, [inputPrograms, enrich]);

  const load = useCallback(async () => {
    if (!userProfile || !inputPrograms || inputPrograms.length === 0) {
      console.warn('🚫 usePerfectMatches: Early return - ', {
        hasUserProfile: !!userProfile,
        hasInputPrograms: !!inputPrograms,
        programsLength: inputPrograms?.length || 0,
        userProfileId: userProfile?.id,
        userProfileHasChildren: !!userProfile?.children,
        childrenCount: userProfile?.children?.length || 0
      });
      setMatches([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const programs = await preparedProgramsPromise;
      const result = await findPerfectMatches(userProfile, programs, {
        maxResults,
        minScore,
      });
      setTotal(result.length);
      setMatches(limitTop ? result.slice(0, limitTop) : result);
    } catch (e) {
      console.error('usePerfectMatches error:', e);
      setError('Unable to load perfect matches');
      setMatches([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [userProfile, inputPrograms, maxResults, minScore, limitTop, preparedProgramsPromise]);

  useLayoutEffect(() => {
    if (userProfile && inputPrograms?.length > 0) {
      setIsLoading(true);
    }
    load();
  }, [load]);

  return { matches, isLoading, error, total };
}


